import {ReactElement, useRef, useState} from "react";
import DOMPurify from "dompurify";
import {ChallengeInfoResponse} from "@devour/client";
import {ChallengeAcceptModal} from "@/pages/challenge/ChallengeAcceptModal";
import {useSelector} from "react-redux";
import {IStore} from "@/redux/defaultStore";
import useWindowSize from "@/hooks/useWindowSize";

// ResponsiveTypography type - matches admin config
interface ResponsiveTypography {
    font?: string;
    desktopSize?: string;
    tabletSize?: string;
    mobileSize?: string;
}

// HeroSection type - matches PageHeroSectionResponse from backend + admin typography config
interface HeroSection {
    heroType?: "IMAGE" | "VIDEO";
    heroImageUrl?: string;
    videoUrl?: string;
    videoPosterImageUrl?: string;
    title?: string;
    description?: string;
    ctaText?: string;
    ctaBackgroundColor?: string;
    ctaTextColor?: string;
    showGradientOverlay?: boolean;
    gradientEndColor?: string;
    gradientOpacity?: number;
    gradientCoverage?: number;
    // Typography settings from admin
    titleTypography?: ResponsiveTypography;
    subtitleTypography?: ResponsiveTypography;
    descriptionTypography?: ResponsiveTypography;
}

// Extended challenge type with heroSection
type ChallengeWithHero = ChallengeInfoResponse & {
    start?: number;
    end?: number;
    heroSection?: HeroSection;
};

interface ChallengeHeroProps {
    challenge?: ChallengeWithHero;
    refetch: () => void;
    onLoginRequired: () => void;
}

// Default typography values (matching admin defaults)
const DEFAULT_TYPOGRAPHY = {
    title: {
        font: "Owners Narrow, sans-serif",
        desktopSize: "6.25rem",
        tabletSize: "3.75rem",
        mobileSize: "3.25rem",
    },
    subtitle: {
        font: "Inter, sans-serif",
        desktopSize: "1.5rem",
        tabletSize: "1.25rem",
        mobileSize: "1rem",
    },
    description: {
        font: "Inter, sans-serif",
        desktopSize: "1.125rem",
        tabletSize: "1rem",
        mobileSize: "0.875rem",
    },
};

// Breakpoints matching Bootstrap/SCSS
const BREAKPOINTS = {
    mobile: 576,
    tablet: 992,
};

export const ChallengeHero = ({challenge, refetch, onLoginRequired}: ChallengeHeroProps): ReactElement => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const windowSize = useWindowSize();

    // Determine current breakpoint
    const getBreakpoint = (): "mobile" | "tablet" | "desktop" => {
        if (windowSize[0] <= BREAKPOINTS.mobile) return "mobile";
        if (windowSize[0] <= BREAKPOINTS.tablet) return "tablet";
        return "desktop";
    };
    const breakpoint = getBreakpoint();

    // Get heroSection config from challenge
    const heroSection = challenge?.heroSection;

    // Media - prioritize heroSection, fallback to challenge base fields
    const heroType = heroSection?.heroType || "IMAGE";
    const heroImageUrl = heroSection?.heroImageUrl || challenge?.backgroundImageLargeUrl;
    const videoUrl = heroSection?.videoUrl;
    const videoPosterUrl = heroSection?.videoPosterImageUrl;

    // Text content - heroSection overrides → challenge base fields
    const title = heroSection?.title || challenge?.name || "";
    const description = heroSection?.description || challenge?.description || "";

    // CTA button - from heroSection or falls back to CSS variable (brand color)
    const ctaText = heroSection?.ctaText || "ENTER NOW";
    const ctaBackgroundColor = heroSection?.ctaBackgroundColor || "var(--challenge-primary)";
    const ctaTextColor = heroSection?.ctaTextColor || "#ffffff";

    // Gradient - only show if explicitly enabled in admin
    const showGradient = heroSection?.showGradientOverlay === true;
    const gradientColor = heroSection?.gradientEndColor || "#000000";
    const gradientOpacity = (heroSection?.gradientOpacity ?? 80) / 100; // Convert 0-100 to 0-1
    const gradientCoverage = heroSection?.gradientCoverage ?? 50;

    // Convert hex color to rgba with opacity
    const hexToRgba = (hex: string, alpha: number): string => {
        const cleanHex = hex.replace("#", "");
        const r = parseInt(cleanHex.substring(0, 2), 16) || 0;
        const g = parseInt(cleanHex.substring(2, 4), 16) || 0;
        const b = parseInt(cleanHex.substring(4, 6), 16) || 0;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    const gradientEnd = hexToRgba(gradientColor, gradientOpacity);

    // Get responsive font size based on breakpoint
    const getResponsiveSize = (typography: ResponsiveTypography | undefined, defaults: typeof DEFAULT_TYPOGRAPHY.title): string => {
        switch (breakpoint) {
            case "mobile":
                return typography?.mobileSize || defaults.mobileSize;
            case "tablet":
                return typography?.tabletSize || defaults.tabletSize;
            default:
                return typography?.desktopSize || defaults.desktopSize;
        }
    };

    // Title typography
    const titleStyle: React.CSSProperties = {
        fontFamily: heroSection?.titleTypography?.font || DEFAULT_TYPOGRAPHY.title.font,
        fontSize: getResponsiveSize(heroSection?.titleTypography, DEFAULT_TYPOGRAPHY.title),
        fontWeight: 900,
        lineHeight: "85%",
        letterSpacing: 0,
        marginBottom: breakpoint === "mobile" ? "16px" : "30px",
        textTransform: "uppercase",
    };

    // Description typography
    const descriptionStyle: React.CSSProperties = {
        fontFamily: heroSection?.descriptionTypography?.font || DEFAULT_TYPOGRAPHY.description.font,
        fontSize: getResponsiveSize(heroSection?.descriptionTypography, DEFAULT_TYPOGRAPHY.description),
        fontWeight: 500,
        lineHeight: "140%",
        letterSpacing: 0,
    };

    /*
     * Gradient style - horizontal gradient from right to left
     * Coverage = how much of the hero is covered by the gradient (0% = none, 100% = full)
     * The uncovered portion is always transparent so the image/video shows through
     */
    const gradientStyle: React.CSSProperties = showGradient
        ? {
            background: `linear-gradient(270deg, transparent 0%, transparent ${100 - gradientCoverage}%, ${gradientEnd} 100%)`,
        }
        : {};

    const handleClick = () => {
        if (!fullToken) {
            onLoginRequired();
            return;
        }

        if (!challenge?.isAccepted) {
            setShowAcceptModal(true);
            return;
        }

        // Scroll to quests section if already accepted
        const questsSection = document.querySelector(".challenge-page_quest-entry-info");
        if (questsSection) {
            questsSection.scrollIntoView({behavior: "smooth"});
        }
    };

    const handleChallengeAccepted = () => {
        setShowAcceptModal(false);
        refetch();
    };

    // Render media (video or image)
    const renderMedia = () => {
        if (heroType === "VIDEO" && videoUrl) {
            return (
                <video
                    ref={videoRef}
                    className="challenge-page_hero_video"
                    src={videoUrl}
                    poster={videoPosterUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                />
            );
        }

        if (heroImageUrl) {
            return (
                <img
                    className="challenge-page_hero_image"
                    src={heroImageUrl}
                    alt={challenge?.name || "Challenge Hero"}
                />
            );
        }

        return null;
    };

    return (
        <div className="challenge-page_hero">
            {renderMedia()}
            <div className="challenge-page_hero_text">
                <div
                    className="challenge-page_hero_text_title"
                    style={titleStyle}
                    /*
                     * Sanitize first, then turn newlines into `<br/>`. Title is
                     * admin-authored (`heroSection.title || challenge.name`) and
                     * could carry `<img onerror=…>` etc. on a compromised admin.
                     * DOMPurify runs before the `<br/>` substitution so it
                     * inspects the raw input and strips any injected tags.
                     */
                    dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(title).replace(/\n/g, "<br/>")}}
                />
                <div
                    className="challenge-page_hero_text_description"
                    style={descriptionStyle}
                >
                    {description}
                </div>
            </div>
            <div className="challenge-page_hero_cta-button">
                <ChallengeAcceptModal
                    challenge={challenge}
                    isVisible={showAcceptModal && !!fullToken && !challenge?.isAccepted}
                    onClose={() => setShowAcceptModal(false)}
                    onAccepted={handleChallengeAccepted}
                    showNewsletterFields={true}
                />
                <button
                    className="challenge-page_enter-now-button"
                    onClick={handleClick}
                    style={{
                        backgroundColor: ctaBackgroundColor,
                        color: ctaTextColor,
                    }}
                >
                    {ctaText}
                </button>
            </div>
            <div
                className="challenge-page_hero_linear-gradient"
                style={gradientStyle}
            />
        </div>
    );
};

export default ChallengeHero;
